import { InvestmentAccount } from '@/hooks/useInvestments';
import type { InvestmentAccountWithConvertedBalance } from '@/hooks/useInvestmentCurrencyConversion';
import { InvestmentCard } from './InvestmentCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp } from 'lucide-react';

interface InvestmentListByTypeProps {
  investmentsByType: Record<string, (InvestmentAccount | InvestmentAccountWithConvertedBalance)[]>;
  currency: string;
  onEdit: (investment: InvestmentAccount) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

/**
 * InvestmentListByType component displays investments grouped by investment type
 * with collapsible sections for each type. Shows type totals and investment counts.
 * 
 * @param investmentsByType - Investments grouped by type ID
 * @param currency - The selected currency for display
 * @param onEdit - Callback function when edit button is clicked on an investment
 * @param onDelete - Callback function when delete button is clicked on an investment
 * @param isLoading - Loading state indicator
 * 
 * @example
 * <InvestmentListByType
 *   investmentsByType={groupedInvestments}
 *   currency="USD"
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 * />
 */
export function InvestmentListByType({
  investmentsByType,
  currency,
  onEdit,
  onDelete,
  isLoading = false,
}: InvestmentListByTypeProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const sortedTypes = Object.keys(investmentsByType).sort((a, b) => {
    const typeA = investmentsByType[a][0]?.investment_type?.name || '';
    const typeB = investmentsByType[b][0]?.investment_type?.name || '';
    return typeA.localeCompare(typeB);
  });

  if (sortedTypes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No investments found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {sortedTypes.map((typeId) => {
        const investments = investmentsByType[typeId];
        const typeName = investments[0]?.investment_type?.name || 'Unknown Type';

        // Calculate total for this investment type, preferring convertedBalance when available
        const typeTotal = investments.reduce(
          (sum, investment) => sum + (('convertedBalance' in investment ? investment.convertedBalance : undefined) ?? investment.balance),
          0
        );

        return (
          <Card key={typeId}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                {typeName}
                <Badge variant="secondary" className="ml-auto text-xs">
                  {investments.length}
                </Badge>
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Total:{' '}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: currency,
                }).format(typeTotal)}
              </div>
            </CardHeader>
            <CardContent>
              <div
                className="space-y-4"
                role="list"
                aria-label={`${typeName} investments`}
              >
                {investments.map((investment) => (
                  <InvestmentCard
                    key={investment.id}
                    investment={investment}
                    currency={currency}
                    onEdit={() => onEdit(investment)}
                    onDelete={() => onDelete(investment.id)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
